import { type SidepanelSide } from '@shared/sidepanel/side'

import { sanitizeSidepanelSectionOrder, type SidepanelSectionKey } from '../../sidepanel-section-order'

/**
 * What a tour step spotlights. Section targets match `data-tour-section`
 * attributes on the Home sections, so a user's custom section order never
 * breaks the lookup. `history` (Recents) is deliberately absent — it stays
 * hidden until the account has activity, which is never true on Day 0.
 */
export type QuickTourTarget = 'toolbar' | 'upcoming' | 'reminders' | 'actions' | 'chat'

export interface TourStepDescriptor {
  target: QuickTourTarget
  title: string
  paragraphs: Array<string>
  /** Substring of `paragraphs` the card renders in bold (the summon hotkey). */
  emphasis?: string
}

export interface TourCopyContext {
  side: SidepanelSide
  triggerHotkey: string
}

/** The tour is always welcome + three sections + chat. */
export const TOTAL_TOUR_STEPS = 5

const SECTION_TARGETS = ['upcoming', 'reminders', 'actions'] as const

type TourSectionTarget = (typeof SECTION_TARGETS)[number]

const isTourSectionTarget = (key: SidepanelSectionKey): key is TourSectionTarget =>
  (SECTION_TARGETS as ReadonlyArray<string>).includes(key)

const SECTION_STEPS: Record<TourSectionTarget, Omit<TourStepDescriptor, 'target'>> = {
  upcoming: {
    title: 'Be prepared for every meeting',
    paragraphs: [
      "Open Upcoming to see what's next on the calendar. Highlight will prepare a brief before a meeting, so the context that matters is ready for you.",
      // Kept true by the meeting tour's own `unseen` flag (see meeting-tour/).
      'After your first meeting, Highlight will offer you a tour of your meeting notes.',
    ],
  },
  reminders: {
    title: 'Keep track of what matters',
    paragraphs: ['Highlight surfaces follow-ups and reminders from your day. Add your own anytime.'],
  },
  actions: {
    title: 'Your next steps ready for you',
    paragraphs: [
      "Highlight drafts follow-ups, messages, and updates from your connected tools, and tells you when they're ready. Review, edit, and act all in one place.",
    ],
  },
}

const buildWelcomeStep = ({ side, triggerHotkey }: TourCopyContext): TourStepDescriptor => ({
  target: 'toolbar',
  title: 'Keep Highlight within reach',
  paragraphs: [
    `Highlight stays at the edge of your screen, ready whenever you need it. Move your mouse to the far ${side} or press ${triggerHotkey} to open it.`,
    'Drag the toolbar to either side of your screen or across monitors, and resize the panel in any direction.',
  ],
  emphasis: triggerHotkey,
})

const CHAT_STEP: TourStepDescriptor = {
  target: 'chat',
  title: 'Find anything across your work',
  paragraphs: [
    "Highlight brings together the context from the screen, your apps, and meetings you've been in to find the information you need.",
  ],
}

/**
 * Ordered tour steps for a user's section layout: welcome first, chat last, and
 * the three section steps in whatever order the user arranged their sections.
 */
export const buildTourSteps = (rawSectionOrder: unknown, copy: TourCopyContext): Array<TourStepDescriptor> => {
  const sectionSteps = sanitizeSidepanelSectionOrder(rawSectionOrder)
    .filter(isTourSectionTarget)
    .map((target) => ({ target, ...SECTION_STEPS[target] }))

  return [buildWelcomeStep(copy), ...sectionSteps, CHAT_STEP]
}
